import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, Stack, Tooltip, Typography } from '@mui/joy';
import {
    CheckCircle,
    Error as ErrorIcon,
    ExpandMore,
    HourglassEmpty,
    Info,
    Warning,
} from '@mui/icons-material';
import { resolveSessionEndsAt } from '../../hooks/useUploadRequest';

const STATUS_META = {
    pending: { color: 'neutral', label: 'Waiting for hub connection', icon: <HourglassEmpty /> },
    in_progress: { color: 'primary', label: 'Uploading…', icon: <Info /> },
    collecting: { color: 'primary', label: 'Collecting…', icon: <Info /> },
    uploading: { color: 'primary', label: 'Uploading collected data…', icon: <Info /> },
    completed: { color: 'success', label: 'Upload complete', icon: <CheckCircle /> },
    partial: { color: 'warning', label: 'Partial upload', icon: <Warning /> },
    failed: { color: 'danger', label: 'Upload failed', icon: <ErrorIcon /> },
    expired: { color: 'danger', label: 'Upload expired', icon: <ErrorIcon /> },
    cancelled: { color: 'neutral', label: 'Cancelled', icon: <Info /> },
};

function formatCountMap(map) {
    if (!map || typeof map !== 'object') return null;
    const parts = Object.entries(map)
        .filter(([, value]) => value != null)
        .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`);
    return parts.length ? parts.join(' · ') : null;
}

export function formatRemaining(ms) {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    if (minutes > 0) {
        return `${minutes}m`;
    }
    return `${seconds}s`;
}

function chipLabel(job, now) {
    if (job?.type === 'collection_session' && job.status === 'completed') {
        return 'Collection complete';
    }
    if (job?.status === 'collecting') {
        const endsAt = resolveSessionEndsAt(job);
        if (endsAt != null) {
            const remaining = endsAt - now;
            if (remaining <= 0) return 'Collecting… ending now';
            return `Collecting… ends in ${formatRemaining(remaining)}`;
        }
        return 'Collecting…';
    }
    return (STATUS_META[job?.status] || {}).label || job?.status || 'Upload';
}

export default function UploadJobStatusChip({ job, error, onRequestAgain }) {
    const [expanded, setExpanded] = useState(false);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (job?.status !== 'collecting') return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [job?.status]);

    if (!job && !error) return null;

    const meta = STATUS_META[job?.status] || {
        color: 'neutral',
        label: job?.status || 'Upload',
        icon: <Info />,
    };
    const label = chipLabel(job, now);
    const rowsUploaded = formatCountMap(job?.rows_uploaded);
    const rowsExpired = formatCountMap(job?.rows_expired_locally);
    const jobError = typeof job?.error === 'string' ? job.error : job?.error?.message;
    const canRetry = job?.status === 'expired' || job?.status === 'failed';

    const tooltipBits = [];
    if (rowsUploaded) tooltipBits.push(`Rows uploaded: ${rowsUploaded}`);
    if (job?.status === 'partial' && jobError) tooltipBits.push(jobError);
    if (job?.status === 'partial' && rowsExpired) tooltipBits.push(`Expired locally: ${rowsExpired}`);
    if (job?.status === 'pending') tooltipBits.push('Waiting for hub connection');
    if (job?.status === 'collecting') {
        const endsAt = resolveSessionEndsAt(job);
        if (endsAt != null) {
            tooltipBits.push(`Ends ${new Date(endsAt).toLocaleString()}`);
        }
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Tooltip title={tooltipBits.join(' — ') || label} placement="bottom-start">
                    <Chip
                        size="sm"
                        variant="soft"
                        color={meta.color}
                        startDecorator={meta.icon}
                        endDecorator={<ExpandMore sx={{ fontSize: 16 }} />}
                        onClick={() => setExpanded((prev) => !prev)}
                        sx={{ cursor: 'pointer' }}
                    >
                        {label}
                    </Chip>
                </Tooltip>
                {error && (
                    <Typography level="body-xs" color="danger">
                        {error}
                    </Typography>
                )}
            </Stack>

            {expanded && job && (
                <Card variant="soft" size="sm" sx={{ mt: 1, p: 1.5, maxWidth: 420 }}>
                    <Stack spacing={0.75}>
                        <Typography level="body-xs" textColor="neutral.600">
                            Job {job.job_id} · {job.type || 'range_upload'}
                        </Typography>
                        {job.status === 'collecting' && (
                            <Typography level="body-sm">{label}</Typography>
                        )}
                        {rowsUploaded && (
                            <Typography level="body-sm">Rows uploaded: {rowsUploaded}</Typography>
                        )}
                        {job.status === 'partial' && rowsExpired && (
                            <Typography level="body-sm">Expired locally: {rowsExpired}</Typography>
                        )}
                        {job.status === 'partial' && jobError && (
                            <Alert size="sm" color="warning" variant="soft">
                                {jobError}
                            </Alert>
                        )}
                        {(job.status === 'failed' || job.status === 'expired') && (
                            <Alert size="sm" color="danger" variant="soft">
                                {jobError || (job.status === 'expired'
                                    ? 'This request expired before the hub claimed it.'
                                    : 'Upload failed.')}
                            </Alert>
                        )}
                        {job.status === 'pending' && (
                            <Typography level="body-sm" textColor="neutral.600">
                                Waiting for hub connection
                            </Typography>
                        )}
                        {canRetry && typeof onRequestAgain === 'function' && (
                            <Button size="sm" variant="outlined" onClick={onRequestAgain}>
                                Create a new request
                            </Button>
                        )}
                    </Stack>
                </Card>
            )}
        </Box>
    );
}
